// The homepage's words for each platform brand.
//
// The domain picks the brand (brand.brand_key; see theme/brands.js), and each brand speaks to
// its own audience: joulelingo.com to schools, kilosight.com to businesses, hospitality,
// landlords and households. Kilosight copy must never mention schools: people tracking their
// own home asked not to see the word, and registry.test.js-style checks in homeCopy.test.js
// hold it to that. 'power' is the pre-launch default host and keeps the original all-audience
// copy.
//
// `{product}` is replaced with the resolved product name, so a partner on a brand's subdomain
// gets its own name in the same sentences.

const POWER = {
    metaDescription:
        '{product} turns your electricity and gas readings into targets, alerts, cost and carbon reporting for schools, landlords, multi-site businesses and partners.',
    eyebrow: 'Energy transparency platform',
    title: 'Understand, share and reduce energy use across your buildings',
    subtitle:
        '{product} connects to your energy suppliers and smart meters, then turns a reading every half hour into the targets, alerts and reports the people in your buildings act on.',
    outcomePoints: [
        'Waste found where nobody was looking: overnight, at weekends, and across holidays and closures',
        'Weather-normalised comparison, so a cold quarter is not mistaken for a failed intervention',
        'Reporting that is ready for boards, governors, owners and funders without rebuilding a spreadsheet'
    ],
    household: {
        title: 'Just tracking your own house?',
        body: '{product} works for a single property too. Connect your smart meter and you get the same half-hourly detail scaled down to one home: where the energy actually goes, what it costs on your tariff, early warning when something unusual starts drawing power, and a what-if slider for trying a change before you commit to it. The school-shaped parts (house competitions, class projects, quizzes) simply are not there.'
    },
    ctaTitle: 'Bring energy transparency to your buildings',
    ctaBody:
        'Start with a single building or roll out across a whole portfolio, with support from onboarding through to adoption.',
    footerTagline:
        'Energy monitoring and reporting for schools, estates and multi-site businesses.'
}

const JOULELINGO = {
    metaDescription:
        '{product} turns your school’s electricity and gas readings into targets, alerts, cost and carbon reporting, and classroom activities built on your own data.',
    eyebrow: 'Energy for schools',
    title: 'Make energy something your whole school understands and acts on',
    subtitle:
        '{product} connects to your school’s smart meters and turns a reading every half hour into targets for the business manager, alerts for the site team, and activities pupils learn from because the data is their own.',
    outcomePoints: [
        'Waste found where nobody was looking: overnight, at weekends and through the holidays',
        'Weather-normalised comparison, so a cold term is not mistaken for a failed intervention',
        'Reporting that is ready for governors, trusts and funders without rebuilding a spreadsheet'
    ],
    // Households are Kilosight's audience; the schools product does not pitch them.
    household: null,
    ctaTitle: 'Bring energy transparency to your school',
    ctaBody:
        'Start with one school or roll out across a whole trust, with support from onboarding through to adoption.',
    footerTagline: 'Energy monitoring, reporting and learning for schools.'
}

// DRAFT for Ross to edit (16 Oct): first pass at the Kilosight voice.
const KILOSIGHT = {
    metaDescription:
        '{product} turns your electricity and gas readings into alerts, cost and carbon reporting for multi-site businesses, hospitality, landlords and homes.',
    eyebrow: 'Energy insight for business',
    title: 'See where your energy goes, and what it is costing you',
    subtitle:
        '{product} connects to the meters you already have and turns a reading every half hour into the alerts, comparisons and cost reports that show which sites are wasting energy, and when.',
    outcomePoints: [
        'Waste found where nobody was looking: overnight, at weekends and while a site is closed',
        'Weather-normalised comparison, so a cold quarter is not mistaken for a failed intervention',
        'Reporting that is ready for owners, boards and tenants without rebuilding a spreadsheet'
    ],
    household: {
        title: 'Just tracking your own home?',
        body: '{product} works for a single property too. Connect your smart meter and you get the same half-hourly detail scaled down to one home: where the energy actually goes, what it costs on your tariff, early warning when something unusual starts drawing power, and a what-if slider for trying a change before you commit to it.'
    },
    ctaTitle: 'Find out what your buildings use when nobody is there',
    ctaBody:
        'Start with a single site or roll out across a whole portfolio, with support from onboarding through to adoption.',
    footerTagline:
        'Energy monitoring and reporting for businesses, landlords and homes.'
}

export const HOME_COPY = {
    power: POWER,
    joulelingo: JOULELINGO,
    kilosight: KILOSIGHT
}

/** The homepage copy for a brand key; anything unknown gets the default host's copy. */
export function homeCopyFor(brandKey) {
    return HOME_COPY[brandKey] || POWER
}

/** Fill `{product}` in one line of copy. */
export function withProduct(text, productName) {
    return text.replaceAll('{product}', productName)
}
